import React from "react";

/* Scan Dialog — opened from every "Scan now" button (topbar / scans screen),
   rendered over a blurred backdrop.

   Two modes:
   - First scan: no presets, defaults skewed deeper (3-day window) since
     there's no baseline; copy frames it as a setup moment.
   - Subsequent scans: 3 preset chips at top (Quick / Catch up / Deep);
     any manual tweak switches to Custom.

   Shared controls: time window, subreddits, content type, match
   floor. The footer shows a (cosmetic) live estimate. onRun receives the
   resolved params; app.jsx translates them into ingest + scan API calls. */

function ScanDialog({
  isOpen,
  onClose,
  onRun,
  profile,
  firstScan,
  lastScanLabel,
  lastScanSurfaced,
}) {
  const planState = (window.usePlan ? window.usePlan() : null) || {
    plan: "free",
    limits: {
      allowed_scan_windows: ["24h", "since-last"],
      monthly_scan_quota: 6,
    },
    usage: null,
  };
  const planLoading = planState?.loading ?? true;
  const isFree = !planLoading && planState?.plan === "free";
  const allowedWindows = isFree
    ? ["since-last", "24h"]
    : (planState?.limits?.allowed_scan_windows || ["since-last", "24h", "3d", "7d", "14d", "30d"]);
  const monthlyCap = planState?.limits?.monthly_scan_quota;
  const used = planState?.usage?.used || 0;
  const remaining = monthlyCap == null ? null : Math.max(0, monthlyCap - used);
  const quotaExhausted = monthlyCap != null && remaining === 0;
  // Free users keep their full subreddit radar visible on the profile, but a
  // scan can only hit 3 of them at a time. Solo/Growth scan the whole radar.
  const FREE_SCAN_SUB_CAP = 3;
  const subScanCap = isFree ? FREE_SCAN_SUB_CAP : null;

  const isWindowAllowed = React.useCallback(
    (w) => allowedWindows.includes(w),
    [allowedWindows]
  );

  const goPricing = (e) => {
    e?.preventDefault?.();
    if (typeof onClose === "function") onClose();
    setTimeout(() => {
      try { window.history.pushState({}, "", "/pricing"); window.dispatchEvent(new PopStateEvent("popstate")); }
      catch (err) { window.location.assign("/pricing"); }
    }, 0);
  };
  const allSubs = React.useMemo(
    () => (profile?.subreddits || [])
      .map((s) => String(s).trim())
      .filter(Boolean)
      .map((s) => (s.startsWith("r/") ? s : "r/" + s)),
    [profile]
  );
  // Themes come from the profile's generated search plan (search_themes).
  const allThemes = React.useMemo(() => {
    const plan = profile?._server?.search_plan;
    const raw = plan && Array.isArray(plan.search_themes)
      ? plan.search_themes
      : [];
    return raw.map((t) => String(t).trim()).filter(Boolean);
  }, [profile]);

  // For free users we pre-select the first N subreddits (radar order, which
  // mergeSubreddits keeps roughly signal-ordered). Paid users get all of them.
  const defaultSubs = subScanCap != null ? allSubs.slice(0, subScanCap) : [...allSubs];

  const FIRST_DEFAULTS = {
    window: isFree ? "24h" : "3d",
    subreddits: [...defaultSubs],
    themes: [...allThemes],
    contentType: "both",
    threshold: 60,
  };
  const PRESETS = {
    quick: {
      key: "quick",
      label: "Quick refresh",
      hint: isFree ? "Last 24 hours" : "Since last scan",
      icon: "Zap",
      params: { window: isFree ? "24h" : "since-last", subreddits: [...defaultSubs], themes: [...allThemes], contentType: "both", threshold: 60 },
    },
    catchup: {
      key: "catchup",
      label: "Catch up",
      hint: isFree ? "Last 24 hours" : "Last 3 days",
      icon: "Refresh",
      params: { window: isFree ? "24h" : "3d", subreddits: [...defaultSubs], themes: [...allThemes], contentType: "both", threshold: 60 },
    },
    deep: {
      key: "deep",
      label: "Deep dive",
      hint: isFree ? "Last 24 hours · broader" : "Last 7 days · broader",
      icon: "Target",
      params: { window: isFree ? "24h" : "7d", subreddits: [...defaultSubs], themes: [...allThemes], contentType: "both", threshold: 55 },
    },
  };

  function sameList(a = [], b = []) {
    if (a.length !== b.length) return false;
    return a.every((value, index) => value === b[index]);
  }

  function presetForParams(nextParams) {
    for (const presetDef of Object.values(PRESETS)) {
      const presetParams = presetDef.params;
      if (
        nextParams.window === presetParams.window &&
        nextParams.contentType === presetParams.contentType &&
        nextParams.threshold === presetParams.threshold &&
        sameList(nextParams.subreddits, presetParams.subreddits) &&
        sameList(nextParams.themes, presetParams.themes)
      ) {
        return presetDef.key;
      }
    }
    return "custom";
  }

  const [preset, setPreset] = React.useState(firstScan ? "custom" : "quick");
  const [params, setParams] = React.useState(() =>
    firstScan ? FIRST_DEFAULTS : PRESETS.quick.params
  );
  const openedWhilePlanLoadingRef = React.useRef(false);

  React.useEffect(() => {
    if (!isOpen) {
      openedWhilePlanLoadingRef.current = false;
      return;
    }
    if (planLoading) openedWhilePlanLoadingRef.current = true;
  }, [isOpen, planLoading]);

  // Reset whenever the dialog opens or mode flips
  React.useEffect(() => {
    if (!isOpen || planLoading) return;
    // If the modal opened after plan state was already loaded, refresh in the
    // background without blanking the current entitlements UI.
    if (!openedWhilePlanLoadingRef.current) {
      try { planState?.refresh?.(); } catch (e) {}
    }
    if (firstScan) {
      setPreset("custom");
      setParams(FIRST_DEFAULTS);
    } else {
      setPreset("quick");
      setParams(PRESETS.quick.params);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, firstScan, profile?.id, planLoading, planState?.plan]);

  // Esc to close
  React.useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  function update(patch) {
    const next = { ...params, ...patch };
    setParams(next);
    setPreset(firstScan ? "custom" : presetForParams(next));
  }

  function pickPreset(key) {
    setPreset(key);
    setParams({ ...PRESETS[key].params });
  }

  function toggleInList(list, value) {
    return list.includes(value) ? list.filter((x) => x !== value) : [...list, value];
  }

  const estimate = computeScanEstimate(params, allSubs.length, allThemes.length);

  if (!isOpen) return null;

  const dateline = new Date().toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).toUpperCase();

  return (
    <div className="scan-dialog-overlay is-open" onMouseDown={onClose}>
      <div
        className="scan-dialog"
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Configure scan"
      >
        {/* Header */}
        <div className="scan-dialog-header">
          <div className="scan-dialog-eyebrow">
            <span className="scan-dialog-eyebrow-dot" />
            {firstScan ? "FIRST SCAN" : "SCAN"} · {dateline}
          </div>
          <button
            className="btn btn-ghost btn-icon scan-dialog-close"
            onClick={onClose}
            title="Close (Esc)"
          >
            <window.I.X size={16} />
          </button>
          <h2 className="scan-dialog-title">
            {firstScan ? "Your first scan" : "Run a new scan"}
          </h2>
          <p className="scan-dialog-sub">
            {firstScan
              ? "We'll sweep your monitored subreddits and surface conversations worth your attention."
              : lastScanLabel
                ? <>Last scan ran <strong>{lastScanLabel}</strong> · surfaced <strong>{lastScanSurfaced ?? 0}</strong> opportunities.</>
                : <>Pick how far back to look and what to scan.</>
            }
          </p>
        </div>

        {/* Presets — subsequent scans only */}
        {!firstScan && (
          <div className="scan-presets" role="tablist" aria-label="Scan presets">
            {Object.values(PRESETS).map((p) => {
              const Ic = window.I[p.icon];
              const isActive = preset === p.key;
              return (
                <button
                  key={p.key}
                  role="tab"
                  aria-selected={isActive}
                  className={"scan-preset " + (isActive ? "is-active" : "")}
                  onClick={() => pickPreset(p.key)}
                >
                  <span className="scan-preset-icon"><Ic size={14} /></span>
                  <span className="scan-preset-body">
                    <span className="scan-preset-label">{p.label}</span>
                    <span className="scan-preset-hint">{p.hint}</span>
                  </span>
                  {isActive && <window.I.Check size={12} className="scan-preset-check" />}
                </button>
              );
            })}
            <button
              role="tab"
              aria-selected={preset === "custom"}
              className={"scan-preset scan-preset-custom " + (preset === "custom" ? "is-active" : "")}
              onClick={() => setPreset("custom")}
            >
              <span className="scan-preset-icon"><window.I.Settings size={14} /></span>
              <span className="scan-preset-body">
                <span className="scan-preset-label">Custom</span>
                <span className="scan-preset-hint">Pick your own</span>
              </span>
              {preset === "custom" && <window.I.Check size={12} className="scan-preset-check" />}
            </button>
          </div>
        )}

        {/* Body — scrolls if tall */}
        <div className="scan-dialog-body scroll">
          {planLoading ? (
            <div
              style={{
                padding: "8px 0 2px",
                display: "flex",
                flexDirection: "column",
                gap: 12,
              }}
            >
              <div className="scan-radio-hint">Loading your scan options…</div>
              <div className="skel" style={{ height: 72, borderRadius: 14 }} />
              <div className="skel" style={{ height: 116, borderRadius: 14 }} />
              <div className="skel" style={{ height: 96, borderRadius: 14 }} />
            </div>
          ) : (
            <>
          {/* Free-plan banner (quota left or exhausted) */}
          {isFree && (
            <div
              style={{
                margin: "0 0 12px",
                padding: "10px 12px",
                borderRadius: 10,
                border: "1px solid var(--accent-border)",
                background: "var(--accent-subtle)",
                display: "flex",
                alignItems: "center",
                gap: 10,
              }}
            >
              <window.I.Sparkles size={13} style={{ color: "var(--accent-soft-text)", flexShrink: 0 }} />
              <span style={{ fontSize: 12.5, color: "var(--accent-soft-text)", lineHeight: 1.5 }}>
                <strong>Free plan</strong> · {monthlyCap != null
                  ? <>{remaining} of {monthlyCap} manual scans left this month.</>
                  : <>You're on a free preview.</>}
              </span>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                style={{ marginLeft: "auto" }}
                onClick={goPricing}
              >
                <window.I.Sparkles size={11} />
                Upgrade
              </button>
            </div>
          )}

          {/* WHEN */}
          <section className="scan-section">
            <div className="scan-section-head">
              <span className="scan-section-label">Time window (Posts + Comments)</span>
            </div>
            <div className="scan-radio-list">
              {!firstScan && (
                <ScanRadio
                  checked={params.window === "since-last"}
                  onChange={() => isWindowAllowed("since-last") ? update({ window: "since-last" }) : goPricing()}
                  label="Since last scan"
                  hint={lastScanLabel ? `~${lastScanLabel}` : "Pick up where you left off"}
                  locked={!isWindowAllowed("since-last")}
                  onLockedClick={goPricing}
                />
              )}
              <ScanRadio
                checked={params.window === "24h"}
                onChange={() => update({ window: "24h" })}
                label="Fresh"
                hint="24 hours · Daily check-in"
                locked={!isWindowAllowed("24h")}
                onLockedClick={goPricing}
              />
              <ScanRadio
                checked={params.window === "3d"}
                onChange={() => isWindowAllowed("3d") ? update({ window: "3d" }) : goPricing()}
                label="Recent"
                hint="3 days · Catch up"
                locked={!isWindowAllowed("3d")}
                onLockedClick={goPricing}
              />
              <ScanRadio
                checked={params.window === "7d"}
                onChange={() => isWindowAllowed("7d") ? update({ window: "7d" }) : goPricing()}
                label="Deep"
                hint="1 week · Looks deeper"
                locked={!isWindowAllowed("7d")}
                onLockedClick={goPricing}
              />
              <ScanRadio
                checked={params.window === "14d"}
                onChange={() => isWindowAllowed("14d") ? update({ window: "14d" }) : goPricing()}
                label="Extended"
                hint="2 weeks · Wider net"
                locked={!isWindowAllowed("14d")}
                onLockedClick={goPricing}
              />
              <ScanRadio
                checked={params.window === "30d"}
                onChange={() => isWindowAllowed("30d") ? update({ window: "30d" }) : goPricing()}
                label="Archive"
                hint="30 days · The full archive"
                locked={!isWindowAllowed("30d")}
                onLockedClick={goPricing}
              />
            </div>
          </section>

          <div className="scan-rule" />

          {/* WHERE */}
          <section className="scan-section">
            <div className="scan-section-head">
              <span className="scan-section-label">
                Where · {params.subreddits.length}/
                {subScanCap != null ? Math.min(allSubs.length, subScanCap) : allSubs.length} subreddits
              </span>
              <div className="scan-section-actions">
                <button
                  className="scan-link"
                  onClick={() =>
                    update({
                      subreddits:
                        subScanCap != null ? allSubs.slice(0, subScanCap) : [...allSubs],
                    })
                  }
                >
                  {subScanCap != null && allSubs.length > subScanCap ? `First ${subScanCap}` : "All"}
                </button>
                <span className="scan-link-sep">/</span>
                <button className="scan-link" onClick={() => update({ subreddits: [] })}>None</button>
              </div>
            </div>
            {isFree && allSubs.length > FREE_SCAN_SUB_CAP && (
              <div
                style={{
                  margin: "0 0 8px",
                  padding: "8px 10px",
                  borderRadius: 8,
                  border: "1px dashed var(--accent-border)",
                  background: "var(--accent-subtle)",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  fontSize: 12,
                  color: "var(--accent-soft-text)",
                  lineHeight: 1.45,
                }}
              >
                <window.I.Lock size={11} style={{ flexShrink: 0 }} />
                <span>
                  Free plan scans <strong>{FREE_SCAN_SUB_CAP}</strong> subreddits per run.
                  Pick the ones you want.
</span>
              </div>
            )}
            <div className="scan-chip-grid">
              {allSubs.length === 0 && (
                <span className="scan-radio-hint">No subreddits on this profile's radar yet.</span>
              )}
              {allSubs.map((s) => {
                const on = params.subreddits.includes(s);
                const lockedForFree =
                  subScanCap != null && !on && params.subreddits.length >= subScanCap;
                return (
                  <button
                    key={s}
                    type="button"
                    className={"scan-chip " + (on ? "is-on " : "") + (lockedForFree ? "is-locked" : "")}
                    style={lockedForFree ? { opacity: 0.55, cursor: "pointer" } : undefined}
                    title={
                      lockedForFree
                        ? `Free plan scans ${FREE_SCAN_SUB_CAP} subreddits. Upgrade to scan more.`
                        : undefined
                    }
                    onClick={() => {
                      if (lockedForFree) { goPricing(); return; }
                      update({ subreddits: toggleInList(params.subreddits, s) });
                    }}
                  >
                    {on && <window.I.Check size={11} />}
                    {lockedForFree && <window.I.Lock size={10} />}
                    {s}
                  </button>
                );
              })}
            </div>
          </section>

          <div className="scan-rule" />

          {/* HOW */}
          <section className="scan-section">
            <div className="scan-section-head">
              <span className="scan-section-label">How</span>
            </div>
            <p style={{ margin: "0 0 12px", fontSize: 12, color: "var(--text-secondary)", lineHeight: 1.65 }}>
              Each post and comment is scored against your profile's pain points, trigger phrases, and topics.
              This is done by combining language similarity, phrase signals, and recency. The match floor controls how strict the cutoff is.
            </p>

            <div className="scan-field">
              <label className="scan-field-label">Content</label>
              <div className="scan-segmented">
                {[
                  { v: "both", l: "Posts + comments" },
                  { v: "posts", l: "Posts only" },
                  { v: "comments", l: "Comments only" },
                ].map((opt) => (
                  <button
                    key={opt.v}
                    type="button"
                    className={"scan-segmented-opt " + (params.contentType === opt.v ? "is-on" : "")}
                    onClick={() => update({ contentType: opt.v })}
                  >
                    {opt.l}
                  </button>
                ))}
              </div>
            </div>

            <div className="scan-field">
              <label className="scan-field-label">
                Match floor
                <span className="scan-field-meta">
                  ≥ <strong>{params.threshold}%</strong> confidence
                </span>
              </label>
              <input
                type="range"
                min="40"
                max="95"
                step="5"
                value={params.threshold}
                onChange={(e) => update({ threshold: parseInt(e.target.value, 10) })}
                className="scan-slider"
              />
              <div className="scan-slider-ticks">
                <span>Broader</span>
                <span>Stricter</span>
              </div>
            </div>
          </section>
            </>
          )}
        </div>

        {/* Footer — estimate + actions */}
        <div className="scan-dialog-footer">
          <div className="scan-estimate" title="Estimated based on profile signal density">
            <div className="scan-estimate-row">
              <span className="scan-estimate-num">{estimate.candidates}</span>
              <span className="scan-estimate-label">candidates</span>
              <span className="scan-estimate-dot" />
              <span className="scan-estimate-num">~{estimate.surfaced}</span>
              <span className="scan-estimate-label">surfaced</span>
              <span className="scan-estimate-dot" />
              <span className="scan-estimate-label scan-estimate-time">
                <window.I.Clock size={11} /> ~{estimate.time}
              </span>
            </div>
          </div>
          <div className="scan-dialog-actions">
            <button className="btn btn-secondary btn-sm" onClick={onClose}>
              Cancel
            </button>
            {quotaExhausted ? (
              <button
                className="btn btn-primary btn-sm"
                onClick={goPricing}
                title="You've used all free scans this month"
              >
                <window.I.Lock size={13} />
                Upgrade to scan
              </button>
            ) : (
              <button
                className="btn btn-primary btn-sm"
                onClick={() => onRun(params)}
                disabled={planLoading || params.subreddits.length === 0}
              >
                <window.I.Sparkles size={13} />
                {planLoading ? "Loading options…" : "Start scan"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ScanRadio({ checked, onChange, label, hint, locked, onLockedClick }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={checked}
      aria-disabled={locked ? "true" : undefined}
      className={"scan-radio " + (checked ? "is-on " : "") + (locked ? "is-locked" : "")}
      style={locked ? { opacity: 0.62, cursor: "pointer", position: "relative" } : undefined}
      onClick={locked ? (onLockedClick || onChange) : onChange}
      title={locked ? "Click to upgrade" : undefined}
    >
      <span className="scan-radio-dot" aria-hidden="true">
        <span className="scan-radio-dot-inner" />
      </span>
      <span className="scan-radio-text">
        <span className="scan-radio-label" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          {label}
          {locked && (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 3,
                padding: "1px 6px",
                borderRadius: 999,
                background: "var(--accent-subtle)",
                color: "var(--accent-soft-text)",
                border: "1px solid var(--accent-border)",
                fontSize: 9.5,
                fontWeight: 700,
                letterSpacing: 0.4,
                textTransform: "uppercase",
              }}
            >
              <window.I.Lock size={9} />
              Upgrade
            </span>
          )}
        </span>
        <span className="scan-radio-hint">{hint}</span>
      </span>
    </button>
  );
}

/* Rough estimate driven by params — cosmetic, but it should respond
   plausibly to user changes. Real candidate/surfaced counts come from the
   backend after the scan runs. */
function computeScanEstimate(p, totalSubs, totalThemes) {
  const winMul = {
    "since-last": 0.35,
    "24h": 0.55,
    "3d": 2.0,
    "7d": 4.4,
    "14d": 8.0,
    "30d": 16.0,
  }[p.window] || 1;

  const subFactor = totalSubs > 0 ? p.subreddits.length / totalSubs : 0;
  const themeFactor = totalThemes > 0 ? p.themes.length / totalThemes : 0;

  let candidates = Math.round(180 * winMul * subFactor * (0.3 + themeFactor * 0.7));
  if (p.contentType === "posts") candidates = Math.round(candidates * 0.55);
  else if (p.contentType === "comments") candidates = Math.round(candidates * 0.7);

  // Higher match floor = a tighter similarity cutoff = fewer candidates.
  const floorMul = Math.max(0.1, 1 - (p.threshold - 40) / 90);
  candidates = Math.max(0, Math.round(candidates * floorMul));

  // Higher threshold = fewer surfaced (steeper falloff)
  const surfaceRate = Math.max(0.015, (100 - p.threshold) / 850);
  const surfaced = candidates === 0 ? 0 : Math.max(1, Math.round(candidates * surfaceRate));

  const seconds = Math.max(8, Math.round(15 + candidates * 0.18));
  const time =
    seconds < 60
      ? `${seconds}s`
      : `${Math.floor(seconds / 60)}m ${seconds % 60}s`;

  return {
    candidates: candidates.toLocaleString(),
    surfaced,
    time,
  };
}

window.ScanDialog = ScanDialog;
